import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Search, Home, Package, Settings, HelpCircle, MessageCircle, ChevronDown, ChevronRight, Tent, Wrench, Leaf, Sparkles, Calendar } from 'lucide-react';
import SearchBar from './SearchBar';
import { slugify } from '../utils/slugs';

// Helper : génère une URL SEO-friendly pour une sous-catégorie
const subcatPath = (categorySlug, name) => `/equipements/${categorySlug}/${slugify(name)}`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCatalogueOpen, setIsCatalogueOpen] = useState(false);
  const [expandedUnivers, setExpandedUnivers] = useState(null);
  const catalogueTimeoutRef = useRef(null);
  const location = useLocation();
  
  const whatsappNumber = "33623893298";
  const whatsappMessage = "Bonjour ! Je souhaite avoir des informations sur vos services de location.";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
    setIsCatalogueOpen(false);
    setExpandedUnivers(null);
  }, [location]);

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  const isActive = (path) => location.pathname === path;

  const toggleUnivers = (univers) => {
    setExpandedUnivers(expandedUnivers === univers ? null : univers);
  };

  const universData = [
    { 
      id: 'evenementiel',
      name: 'Evenementiel - Barnum', 
      slug: 'evenementiel-barnum',
      icon: Tent,
      subcategories: [
        'Location Barnums & Tentes',
        'Location Plancher de réception',
        'Location Tente pliantes',
        'Location chaises pliantes',
        'Location Tables pliantes',
        'Location Vaisselle',
        'Location chauffage',
        'Location Son et Lumière',
        'Location structure Gonflable',
        'Location Matériel Kermesse enfants',
        'Location Tireuse à bières',
        'Matériel Restauration',
        'Location décoration',
        'Location Jeux',
        'Pack Barnum',
      ].map(name => ({ name, path: subcatPath('evenementiel-barnum', name) }))
    },
    { 
      id: 'bricolage',
      name: 'Bricolage - Travaux', 
      slug: 'bricolage-travaux',
      icon: Wrench,
      subcategories: [
        'Location Minipelle',
        'Location Bétonnière',
        'Location Plaque vibrante',
        'Location Marteau piqueur, burineur',
        'Location Groupe électrogène',
        'Location échafaudage',
        'Location Scie tous matériaux',
        'Location ponceuse',
        'Location décolleuse',
        'Location lève-plaque',
        'Location coupe carreaux carrelage',
        'Location compresseur',
        'Location pistolet à peinture',
        'Location déshumidifcateur',
        'Location caméra thermique',
        'Location détecteurs',
        'Location outillages',
      ].map(name => ({ name, path: subcatPath('bricolage-travaux', name) }))
    },
    { 
      id: 'jardinage',
      name: 'Jardinage - Motoculture', 
      slug: 'jardinage-motoculture',
      icon: Leaf,
      subcategories: [
        'Location Broyeur de végétaux',
        'Location fendeuse de buches',
        'Location tarière',
        'Location débroussailleuse',
        'Location Motoculteur & motobineuse',
        'Location scarificateur',
        'Location rouleau à gazon et semoir',
        'Location souffleur',
        'Location taille-haie',
        'Location tronçonneuse & élagueuse',
      ].map(name => ({ name, path: subcatPath('jardinage-motoculture', name) }))
    },
    { 
      id: 'nettoyage',
      name: 'Nettoyage - Logement', 
      slug: 'nettoyage-logement',
      icon: Sparkles,
      subcategories: [
        'Location Shampouineuse',
        'Location aspirateur',
        'Location Nettoyeur',
      ].map(name => ({ name, path: subcatPath('nettoyage-logement', name) }))
    },
  ];

  const categories = [
    { path: '/univers/evenementiel-barnum', label: 'Evenementiel - Barnum', icon: Tent },
    { path: '/univers/bricolage-travaux', label: 'Bricolage - Travaux', icon: Wrench },
    { path: '/univers/jardinage-motoculture', label: 'Jardinage - Motoculture', icon: Leaf },
    { path: '/univers/nettoyage-logement', label: 'Nettoyage - Logement', icon: Sparkles },
  ];

  const navLinks = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/univers', label: 'Catalogues', icon: Package },
    { path: '/services', label: 'Nos Services', icon: Settings },
    { path: '/faq', label: 'Questions fréquentes', icon: HelpCircle },
    { path: '/contact', label: 'Contact', icon: MessageCircle },
  ];

  return (
    <>
      {/* Main header */}
      <header 
        className={`sticky top-0 z-40 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg' 
            : 'bg-white border-b border-slate-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left side - Menu + Logo + Search */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Ouvrir le menu"
              >
                <Menu size={22} />
              </button>

              <Link to="/" className="flex items-center">
                <img 
                  src="/media/logo-cmjn-long-3dloc-high-95e288e8.jpg" 
                  alt="3DLoc" 
                  className="h-10 sm:h-12 w-auto object-contain"
                />
              </Link>

              {/* Modification #0: Barre de recherche agrandie */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors min-w-[180px] md:min-w-[240px]"
                aria-label="Rechercher"
              >
                <Search size={20} />
                <span className="text-sm md:text-base">Rechercher</span>
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              <div 
                className="relative"
                onMouseEnter={() => {
                  if (catalogueTimeoutRef.current) {
                    clearTimeout(catalogueTimeoutRef.current);
                  }
                  setIsCatalogueOpen(true);
                }}
                onMouseLeave={() => {
                  catalogueTimeoutRef.current = setTimeout(() => {
                    setIsCatalogueOpen(false);
                  }, 300);
                }}
              >
                <Link
                  to="/univers"
                  className={`flex items-center gap-1 font-medium transition-colors ${
                    isActive('/univers') || isActive('/categories') ? 'text-green-600' : 'text-slate-700 hover:text-green-600'
                  }`}
                >
                  Catalogues
                  <ChevronDown size={16} className={`transition-transform ${isCatalogueOpen ? 'rotate-180' : ''}`} />
                </Link>

                {isCatalogueOpen && (
                  <div className="absolute top-full left-0 pt-2 w-56 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2">
                      {categories.map((cat) => {
                        const IconComponent = cat.icon;
                        return (
                          <Link
                            key={cat.path}
                            to={cat.path}
                            className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <IconComponent size={18} className="text-green-500" />
                            {cat.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/services"
                className={`font-medium transition-colors ${
                  isActive('/services') ? 'text-green-600' : 'text-slate-700 hover:text-green-600'
                }`}
              >
                Nos Services
              </Link>

              <Link
                to="/faq"
                className={`font-medium transition-colors ${
                  isActive('/faq') ? 'text-green-600' : 'text-slate-700 hover:text-green-600'
                }`}
              >
                Questions fréquentes
              </Link>

              <Link
                to="/contact"
                className={`font-medium transition-colors ${
                  isActive('/contact') ? 'text-green-600' : 'text-slate-700 hover:text-green-600'
                }`}
              >
                Contact
              </Link>
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2">
              <a
                href={`https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="WhatsApp"
              >
                <Phone size={20} />
              </a>

              <a
                href="https://3dloc.lokki.rent/"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              >
                <Calendar size={18} />
                <span className="hidden md:inline">Réserver en 2min, ici</span>
                <span className="md:hidden">Réserver</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      {isSidebarOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setIsSidebarOpen(false)}
          />
          <div className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 overflow-y-auto">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <img 
                src="/media/logo-cmjn-long-3dloc-high-95e288e8.jpg" 
                alt="3DLoc" 
                className="h-10 w-auto"
              />
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
              >
                <X size={22} />
              </button>
            </div>

            {/* Navigation */}
            <nav className="p-4">
              {/* Nos équipements - En premier */}
              <div className="mb-6">
                <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Nos univers
                </p>
                
                {universData.map((univers) => {
                  const IconComponent = univers.icon;
                  const isExpanded = expandedUnivers === univers.id;
                  
                  return (
                    <div key={univers.id} className="mb-2">
                      <button
                        onClick={() => toggleUnivers(univers.id)}
                        className="w-full flex items-center justify-between px-4 py-3 text-slate-700 font-semibold hover:bg-slate-50 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <IconComponent size={18} className="text-green-600" />
                          </div>
                          <span>{univers.name}</span>
                        </div>
                        <ChevronDown 
                          size={18} 
                          className={`text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                      
                      {/* Subcategories - Accordion */}
                      {isExpanded && (
                        <div className="ml-4 mt-1 space-y-1 border-l-2 border-green-200 pl-4">
                          <Link 
                            to={`/univers/${univers.slug}`}
                            className="block px-3 py-2 text-sm text-green-600 font-medium hover:bg-green-50 rounded-lg"
                          >
                            Voir tout
                          </Link>
                          {univers.subcategories.map((sub, index) => (
                            <Link 
                              key={index}
                              to={sub.path}
                              className="block px-3 py-2 text-sm text-slate-600 hover:text-green-600 hover:bg-slate-50 rounded-lg transition-colors"
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Navigation links */}
              <div className="pt-6 border-t border-slate-100">
                <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Navigation
                </p>
                <div className="space-y-1">
                  {navLinks.map((link) => {
                    const IconComponent = link.icon;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                          isActive(link.path)
                            ? 'bg-green-50 text-green-600 font-medium'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <IconComponent size={18} />
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <a
                  href={`https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                >
                  <MessageCircle size={20} />
                  Demander un devis gratuit
                </a>
                <a
                  href="tel:+33623893298"
                  className="flex items-center justify-center gap-2 w-full mt-3 px-4 py-3 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Phone size={20} />
                  06 23 89 32 98
                </a>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Floating Search Button - Right Side */}
      <button
        onClick={() => setIsSearchOpen(true)}
        className="hidden lg:flex fixed right-0 top-1/2 -translate-y-1/2 z-30 flex-col items-center gap-1 px-2 py-4 bg-green-600 text-white rounded-l-xl shadow-lg hover:bg-green-700 transition-all hover:px-3"
        aria-label="Rechercher"
      >
        <Search size={20} />
        <span className="text-xs font-medium writing-vertical" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
          Rechercher
        </span>
      </button>

      {/* Search Modal */}
      <SearchBar isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Header;
